import { IntlShape } from 'react-intl';
import { TranslationKey } from '../translations';
import { OptionGroup } from '../account/ComparisonCalculator/select/Select';

type Country = {
  iso2Code: string;
  translationKey: string;
  continent:
    | 'EUROPE'
    | 'ASIA'
    | 'AFRICA'
    | 'NORTH_AMERICA'
    | 'SOUTH_AMERICA'
    | 'OCEANIA'
    | 'ANTARCTICA';
};

const mapCountriesToOptions = (continent: string, intl: IntlShape) => {
  const options = countries
    .filter((country) => country.continent === continent)
    .map((country) => ({
      value: country.iso2Code,
      label: intl.formatMessage({ id: country.translationKey as TranslationKey }),
    }));

  if (continent === 'EUROPE') {
    const priorityOrder = ['EE', 'FI'];
    const priorityCountries = priorityOrder
      .map((code) => options.find((opt) => opt.value === code))
      .filter((opt): opt is { value: string; label: string } => opt !== undefined);
    const otherCountries = options
      .filter((opt) => !priorityOrder.includes(opt.value))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...priorityCountries, ...otherCountries];
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
};

export const mapCountriesToGroupedOptions = (intl: IntlShape): OptionGroup[] => [
  {
    label: intl.formatMessage({ id: 'continents.EUROPE' }),
    options: mapCountriesToOptions('EUROPE', intl),
  },
  {
    label: intl.formatMessage({ id: 'continents.ASIA' }),
    options: mapCountriesToOptions('ASIA', intl),
  },
  {
    label: intl.formatMessage({ id: 'continents.AFRICA' }),
    options: mapCountriesToOptions('AFRICA', intl),
  },
  {
    label: intl.formatMessage({ id: 'continents.NORTH_AMERICA' }),
    options: mapCountriesToOptions('NORTH_AMERICA', intl),
  },
  {
    label: intl.formatMessage({ id: 'continents.SOUTH_AMERICA' }),
    options: mapCountriesToOptions('SOUTH_AMERICA', intl),
  },
  {
    label: intl.formatMessage({ id: 'continents.OCEANIA' }),
    options: mapCountriesToOptions('OCEANIA', intl),
  },
  {
    label: intl.formatMessage({ id: 'continents.ANTARCTICA' }),
    options: mapCountriesToOptions('ANTARCTICA', intl),
  },
];

export const countries: Country[] = [
  { iso2Code: 'AF', translationKey: 'countries.AF', continent: 'ASIA' },
  { iso2Code: 'AX', translationKey: 'countries.AX', continent: 'EUROPE' },
  { iso2Code: 'AL', translationKey: 'countries.AL', continent: 'EUROPE' },
  { iso2Code: 'DZ', translationKey: 'countries.DZ', continent: 'AFRICA' },
  { iso2Code: 'AS', translationKey: 'countries.AS', continent: 'OCEANIA' },
  { iso2Code: 'AD', translationKey: 'countries.AD', continent: 'EUROPE' },
  { iso2Code: 'AO', translationKey: 'countries.AO', continent: 'AFRICA' },
  { iso2Code: 'AI', translationKey: 'countries.AI', continent: 'NORTH_AMERICA' },
  { iso2Code: 'AQ', translationKey: 'countries.AQ', continent: 'ANTARCTICA' },
  { iso2Code: 'AG', translationKey: 'countries.AG', continent: 'NORTH_AMERICA' },
  { iso2Code: 'AR', translationKey: 'countries.AR', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'AM', translationKey: 'countries.AM', continent: 'ASIA' },
  { iso2Code: 'AW', translationKey: 'countries.AW', continent: 'NORTH_AMERICA' },
  { iso2Code: 'AU', translationKey: 'countries.AU', continent: 'OCEANIA' },
  { iso2Code: 'AT', translationKey: 'countries.AT', continent: 'EUROPE' },
  { iso2Code: 'AZ', translationKey: 'countries.AZ', continent: 'ASIA' },
  { iso2Code: 'BS', translationKey: 'countries.BS', continent: 'NORTH_AMERICA' },
  { iso2Code: 'BH', translationKey: 'countries.BH', continent: 'ASIA' },
  { iso2Code: 'BD', translationKey: 'countries.BD', continent: 'ASIA' },
  { iso2Code: 'BB', translationKey: 'countries.BB', continent: 'NORTH_AMERICA' },
  { iso2Code: 'BY', translationKey: 'countries.BY', continent: 'EUROPE' },
  { iso2Code: 'BE', translationKey: 'countries.BE', continent: 'EUROPE' },
  { iso2Code: 'BZ', translationKey: 'countries.BZ', continent: 'NORTH_AMERICA' },
  { iso2Code: 'BJ', translationKey: 'countries.BJ', continent: 'AFRICA' },
  { iso2Code: 'BM', translationKey: 'countries.BM', continent: 'NORTH_AMERICA' },
  { iso2Code: 'BT', translationKey: 'countries.BT', continent: 'ASIA' },
  { iso2Code: 'BO', translationKey: 'countries.BO', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'BQ', translationKey: 'countries.BQ', continent: 'NORTH_AMERICA' },
  { iso2Code: 'BA', translationKey: 'countries.BA', continent: 'EUROPE' },
  { iso2Code: 'BW', translationKey: 'countries.BW', continent: 'AFRICA' },
  { iso2Code: 'BV', translationKey: 'countries.BV', continent: 'ANTARCTICA' },
  { iso2Code: 'BR', translationKey: 'countries.BR', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'IO', translationKey: 'countries.IO', continent: 'ASIA' },
  { iso2Code: 'BN', translationKey: 'countries.BN', continent: 'ASIA' },
  { iso2Code: 'BG', translationKey: 'countries.BG', continent: 'EUROPE' },
  { iso2Code: 'BF', translationKey: 'countries.BF', continent: 'AFRICA' },
  { iso2Code: 'BI', translationKey: 'countries.BI', continent: 'AFRICA' },
  { iso2Code: 'CV', translationKey: 'countries.CV', continent: 'AFRICA' },
  { iso2Code: 'KH', translationKey: 'countries.KH', continent: 'ASIA' },
  { iso2Code: 'CM', translationKey: 'countries.CM', continent: 'AFRICA' },
  { iso2Code: 'CA', translationKey: 'countries.CA', continent: 'NORTH_AMERICA' },
  { iso2Code: 'KY', translationKey: 'countries.KY', continent: 'NORTH_AMERICA' },
  { iso2Code: 'CF', translationKey: 'countries.CF', continent: 'AFRICA' },
  { iso2Code: 'TD', translationKey: 'countries.TD', continent: 'AFRICA' },
  { iso2Code: 'CL', translationKey: 'countries.CL', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'CN', translationKey: 'countries.CN', continent: 'ASIA' },
  { iso2Code: 'CX', translationKey: 'countries.CX', continent: 'ASIA' },
  { iso2Code: 'CC', translationKey: 'countries.CC', continent: 'ASIA' },
  { iso2Code: 'CO', translationKey: 'countries.CO', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'KM', translationKey: 'countries.KM', continent: 'AFRICA' },
  { iso2Code: 'CG', translationKey: 'countries.CG', continent: 'AFRICA' },
  { iso2Code: 'CD', translationKey: 'countries.CD', continent: 'AFRICA' },
  { iso2Code: 'CK', translationKey: 'countries.CK', continent: 'OCEANIA' },
  { iso2Code: 'CR', translationKey: 'countries.CR', continent: 'NORTH_AMERICA' },
  { iso2Code: 'CI', translationKey: 'countries.CI', continent: 'AFRICA' },
  { iso2Code: 'HR', translationKey: 'countries.HR', continent: 'EUROPE' },
  { iso2Code: 'CU', translationKey: 'countries.CU', continent: 'NORTH_AMERICA' },
  { iso2Code: 'CW', translationKey: 'countries.CW', continent: 'NORTH_AMERICA' },
  { iso2Code: 'CY', translationKey: 'countries.CY', continent: 'EUROPE' },
  { iso2Code: 'CZ', translationKey: 'countries.CZ', continent: 'EUROPE' },
  { iso2Code: 'DK', translationKey: 'countries.DK', continent: 'EUROPE' },
  { iso2Code: 'DJ', translationKey: 'countries.DJ', continent: 'AFRICA' },
  { iso2Code: 'DM', translationKey: 'countries.DM', continent: 'NORTH_AMERICA' },
  { iso2Code: 'DO', translationKey: 'countries.DO', continent: 'NORTH_AMERICA' },
  { iso2Code: 'EC', translationKey: 'countries.EC', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'EG', translationKey: 'countries.EG', continent: 'AFRICA' },
  { iso2Code: 'SV', translationKey: 'countries.SV', continent: 'NORTH_AMERICA' },
  { iso2Code: 'GQ', translationKey: 'countries.GQ', continent: 'AFRICA' },
  { iso2Code: 'ER', translationKey: 'countries.ER', continent: 'AFRICA' },
  { iso2Code: 'EE', translationKey: 'countries.EE', continent: 'EUROPE' },
  { iso2Code: 'SZ', translationKey: 'countries.SZ', continent: 'AFRICA' },
  { iso2Code: 'ET', translationKey: 'countries.ET', continent: 'AFRICA' },
  { iso2Code: 'FK', translationKey: 'countries.FK', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'FO', translationKey: 'countries.FO', continent: 'EUROPE' },
  { iso2Code: 'FJ', translationKey: 'countries.FJ', continent: 'OCEANIA' },
  { iso2Code: 'FI', translationKey: 'countries.FI', continent: 'EUROPE' },
  { iso2Code: 'FR', translationKey: 'countries.FR', continent: 'EUROPE' },
  { iso2Code: 'GF', translationKey: 'countries.GF', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'PF', translationKey: 'countries.PF', continent: 'OCEANIA' },
  { iso2Code: 'TF', translationKey: 'countries.TF', continent: 'ANTARCTICA' },
  { iso2Code: 'GA', translationKey: 'countries.GA', continent: 'AFRICA' },
  { iso2Code: 'GM', translationKey: 'countries.GM', continent: 'AFRICA' },
  { iso2Code: 'GE', translationKey: 'countries.GE', continent: 'ASIA' },
  { iso2Code: 'DE', translationKey: 'countries.DE', continent: 'EUROPE' },
  { iso2Code: 'GH', translationKey: 'countries.GH', continent: 'AFRICA' },
  { iso2Code: 'GI', translationKey: 'countries.GI', continent: 'EUROPE' },
  { iso2Code: 'GR', translationKey: 'countries.GR', continent: 'EUROPE' },
  { iso2Code: 'GL', translationKey: 'countries.GL', continent: 'NORTH_AMERICA' },
  { iso2Code: 'GD', translationKey: 'countries.GD', continent: 'NORTH_AMERICA' },
  { iso2Code: 'GP', translationKey: 'countries.GP', continent: 'NORTH_AMERICA' },
  { iso2Code: 'GU', translationKey: 'countries.GU', continent: 'OCEANIA' },
  { iso2Code: 'GT', translationKey: 'countries.GT', continent: 'NORTH_AMERICA' },
  { iso2Code: 'GG', translationKey: 'countries.GG', continent: 'EUROPE' },
  { iso2Code: 'GN', translationKey: 'countries.GN', continent: 'AFRICA' },
  { iso2Code: 'GW', translationKey: 'countries.GW', continent: 'AFRICA' },
  { iso2Code: 'GY', translationKey: 'countries.GY', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'HT', translationKey: 'countries.HT', continent: 'NORTH_AMERICA' },
  { iso2Code: 'HM', translationKey: 'countries.HM', continent: 'ANTARCTICA' },
  { iso2Code: 'VA', translationKey: 'countries.VA', continent: 'EUROPE' },
  { iso2Code: 'HN', translationKey: 'countries.HN', continent: 'NORTH_AMERICA' },
  { iso2Code: 'HK', translationKey: 'countries.HK', continent: 'ASIA' },
  { iso2Code: 'HU', translationKey: 'countries.HU', continent: 'EUROPE' },
  { iso2Code: 'IS', translationKey: 'countries.IS', continent: 'EUROPE' },
  { iso2Code: 'IN', translationKey: 'countries.IN', continent: 'ASIA' },
  { iso2Code: 'ID', translationKey: 'countries.ID', continent: 'ASIA' },
  { iso2Code: 'IR', translationKey: 'countries.IR', continent: 'ASIA' },
  { iso2Code: 'IQ', translationKey: 'countries.IQ', continent: 'ASIA' },
  { iso2Code: 'IE', translationKey: 'countries.IE', continent: 'EUROPE' },
  { iso2Code: 'IM', translationKey: 'countries.IM', continent: 'EUROPE' },
  { iso2Code: 'IL', translationKey: 'countries.IL', continent: 'ASIA' },
  { iso2Code: 'IT', translationKey: 'countries.IT', continent: 'EUROPE' },
  { iso2Code: 'JM', translationKey: 'countries.JM', continent: 'NORTH_AMERICA' },
  { iso2Code: 'JP', translationKey: 'countries.JP', continent: 'ASIA' },
  { iso2Code: 'JE', translationKey: 'countries.JE', continent: 'EUROPE' },
  { iso2Code: 'JO', translationKey: 'countries.JO', continent: 'ASIA' },
  { iso2Code: 'KZ', translationKey: 'countries.KZ', continent: 'ASIA' },
  { iso2Code: 'KE', translationKey: 'countries.KE', continent: 'AFRICA' },
  { iso2Code: 'KI', translationKey: 'countries.KI', continent: 'OCEANIA' },
  { iso2Code: 'KP', translationKey: 'countries.KP', continent: 'ASIA' },
  { iso2Code: 'KR', translationKey: 'countries.KR', continent: 'ASIA' },
  { iso2Code: 'KW', translationKey: 'countries.KW', continent: 'ASIA' },
  { iso2Code: 'KG', translationKey: 'countries.KG', continent: 'ASIA' },
  { iso2Code: 'LA', translationKey: 'countries.LA', continent: 'ASIA' },
  { iso2Code: 'LV', translationKey: 'countries.LV', continent: 'EUROPE' },
  { iso2Code: 'LB', translationKey: 'countries.LB', continent: 'ASIA' },
  { iso2Code: 'LS', translationKey: 'countries.LS', continent: 'AFRICA' },
  { iso2Code: 'LR', translationKey: 'countries.LR', continent: 'AFRICA' },
  { iso2Code: 'LY', translationKey: 'countries.LY', continent: 'AFRICA' },
  { iso2Code: 'LI', translationKey: 'countries.LI', continent: 'EUROPE' },
  { iso2Code: 'LT', translationKey: 'countries.LT', continent: 'EUROPE' },
  { iso2Code: 'LU', translationKey: 'countries.LU', continent: 'EUROPE' },
  { iso2Code: 'MO', translationKey: 'countries.MO', continent: 'ASIA' },
  { iso2Code: 'MG', translationKey: 'countries.MG', continent: 'AFRICA' },
  { iso2Code: 'MW', translationKey: 'countries.MW', continent: 'AFRICA' },
  { iso2Code: 'MY', translationKey: 'countries.MY', continent: 'ASIA' },
  { iso2Code: 'MV', translationKey: 'countries.MV', continent: 'ASIA' },
  { iso2Code: 'ML', translationKey: 'countries.ML', continent: 'AFRICA' },
  { iso2Code: 'MT', translationKey: 'countries.MT', continent: 'EUROPE' },
  { iso2Code: 'MH', translationKey: 'countries.MH', continent: 'OCEANIA' },
  { iso2Code: 'MQ', translationKey: 'countries.MQ', continent: 'NORTH_AMERICA' },
  { iso2Code: 'MR', translationKey: 'countries.MR', continent: 'AFRICA' },
  { iso2Code: 'MU', translationKey: 'countries.MU', continent: 'AFRICA' },
  { iso2Code: 'YT', translationKey: 'countries.YT', continent: 'AFRICA' },
  { iso2Code: 'MX', translationKey: 'countries.MX', continent: 'NORTH_AMERICA' },
  { iso2Code: 'FM', translationKey: 'countries.FM', continent: 'OCEANIA' },
  { iso2Code: 'MD', translationKey: 'countries.MD', continent: 'EUROPE' },
  { iso2Code: 'MC', translationKey: 'countries.MC', continent: 'EUROPE' },
  { iso2Code: 'MN', translationKey: 'countries.MN', continent: 'ASIA' },
  { iso2Code: 'ME', translationKey: 'countries.ME', continent: 'EUROPE' },
  { iso2Code: 'MS', translationKey: 'countries.MS', continent: 'NORTH_AMERICA' },
  { iso2Code: 'MA', translationKey: 'countries.MA', continent: 'AFRICA' },
  { iso2Code: 'MZ', translationKey: 'countries.MZ', continent: 'AFRICA' },
  { iso2Code: 'MM', translationKey: 'countries.MM', continent: 'ASIA' },
  { iso2Code: 'NA', translationKey: 'countries.NA', continent: 'AFRICA' },
  { iso2Code: 'NR', translationKey: 'countries.NR', continent: 'OCEANIA' },
  { iso2Code: 'NP', translationKey: 'countries.NP', continent: 'ASIA' },
  { iso2Code: 'NL', translationKey: 'countries.NL', continent: 'EUROPE' },
  { iso2Code: 'NC', translationKey: 'countries.NC', continent: 'OCEANIA' },
  { iso2Code: 'NZ', translationKey: 'countries.NZ', continent: 'OCEANIA' },
  { iso2Code: 'NI', translationKey: 'countries.NI', continent: 'NORTH_AMERICA' },
  { iso2Code: 'NE', translationKey: 'countries.NE', continent: 'AFRICA' },
  { iso2Code: 'NG', translationKey: 'countries.NG', continent: 'AFRICA' },
  { iso2Code: 'NU', translationKey: 'countries.NU', continent: 'OCEANIA' },
  { iso2Code: 'NF', translationKey: 'countries.NF', continent: 'OCEANIA' },
  { iso2Code: 'MK', translationKey: 'countries.MK', continent: 'EUROPE' },
  { iso2Code: 'MP', translationKey: 'countries.MP', continent: 'OCEANIA' },
  { iso2Code: 'NO', translationKey: 'countries.NO', continent: 'EUROPE' },
  { iso2Code: 'OM', translationKey: 'countries.OM', continent: 'ASIA' },
  { iso2Code: 'PK', translationKey: 'countries.PK', continent: 'ASIA' },
  { iso2Code: 'PW', translationKey: 'countries.PW', continent: 'OCEANIA' },
  { iso2Code: 'PS', translationKey: 'countries.PS', continent: 'ASIA' },
  { iso2Code: 'PA', translationKey: 'countries.PA', continent: 'NORTH_AMERICA' },
  { iso2Code: 'PG', translationKey: 'countries.PG', continent: 'OCEANIA' },
  { iso2Code: 'PY', translationKey: 'countries.PY', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'PE', translationKey: 'countries.PE', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'PH', translationKey: 'countries.PH', continent: 'ASIA' },
  { iso2Code: 'PN', translationKey: 'countries.PN', continent: 'OCEANIA' },
  { iso2Code: 'PL', translationKey: 'countries.PL', continent: 'EUROPE' },
  { iso2Code: 'PT', translationKey: 'countries.PT', continent: 'EUROPE' },
  { iso2Code: 'PR', translationKey: 'countries.PR', continent: 'NORTH_AMERICA' },
  { iso2Code: 'QA', translationKey: 'countries.QA', continent: 'ASIA' },
  { iso2Code: 'RE', translationKey: 'countries.RE', continent: 'AFRICA' },
  { iso2Code: 'RO', translationKey: 'countries.RO', continent: 'EUROPE' },
  { iso2Code: 'RU', translationKey: 'countries.RU', continent: 'EUROPE' },
  { iso2Code: 'RW', translationKey: 'countries.RW', continent: 'AFRICA' },
  { iso2Code: 'BL', translationKey: 'countries.BL', continent: 'NORTH_AMERICA' },
  { iso2Code: 'SH', translationKey: 'countries.SH', continent: 'AFRICA' },
  { iso2Code: 'KN', translationKey: 'countries.KN', continent: 'NORTH_AMERICA' },
  { iso2Code: 'LC', translationKey: 'countries.LC', continent: 'NORTH_AMERICA' },
  { iso2Code: 'MF', translationKey: 'countries.MF', continent: 'NORTH_AMERICA' },
  { iso2Code: 'PM', translationKey: 'countries.PM', continent: 'NORTH_AMERICA' },
  { iso2Code: 'VC', translationKey: 'countries.VC', continent: 'NORTH_AMERICA' },
  { iso2Code: 'WS', translationKey: 'countries.WS', continent: 'OCEANIA' },
  { iso2Code: 'SM', translationKey: 'countries.SM', continent: 'EUROPE' },
  { iso2Code: 'ST', translationKey: 'countries.ST', continent: 'AFRICA' },
  { iso2Code: 'SA', translationKey: 'countries.SA', continent: 'ASIA' },
  { iso2Code: 'SN', translationKey: 'countries.SN', continent: 'AFRICA' },
  { iso2Code: 'RS', translationKey: 'countries.RS', continent: 'EUROPE' },
  { iso2Code: 'SC', translationKey: 'countries.SC', continent: 'AFRICA' },
  { iso2Code: 'SL', translationKey: 'countries.SL', continent: 'AFRICA' },
  { iso2Code: 'SG', translationKey: 'countries.SG', continent: 'ASIA' },
  { iso2Code: 'SX', translationKey: 'countries.SX', continent: 'NORTH_AMERICA' },
  { iso2Code: 'SK', translationKey: 'countries.SK', continent: 'EUROPE' },
  { iso2Code: 'SI', translationKey: 'countries.SI', continent: 'EUROPE' },
  { iso2Code: 'SB', translationKey: 'countries.SB', continent: 'OCEANIA' },
  { iso2Code: 'SO', translationKey: 'countries.SO', continent: 'AFRICA' },
  { iso2Code: 'ZA', translationKey: 'countries.ZA', continent: 'AFRICA' },
  { iso2Code: 'GS', translationKey: 'countries.GS', continent: 'ANTARCTICA' },
  { iso2Code: 'SS', translationKey: 'countries.SS', continent: 'AFRICA' },
  { iso2Code: 'ES', translationKey: 'countries.ES', continent: 'EUROPE' },
  { iso2Code: 'LK', translationKey: 'countries.LK', continent: 'ASIA' },
  { iso2Code: 'SD', translationKey: 'countries.SD', continent: 'AFRICA' },
  { iso2Code: 'SR', translationKey: 'countries.SR', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'SJ', translationKey: 'countries.SJ', continent: 'EUROPE' },
  { iso2Code: 'SE', translationKey: 'countries.SE', continent: 'EUROPE' },
  { iso2Code: 'CH', translationKey: 'countries.CH', continent: 'EUROPE' },
  { iso2Code: 'SY', translationKey: 'countries.SY', continent: 'ASIA' },
  { iso2Code: 'TW', translationKey: 'countries.TW', continent: 'ASIA' },
  { iso2Code: 'TJ', translationKey: 'countries.TJ', continent: 'ASIA' },
  { iso2Code: 'TZ', translationKey: 'countries.TZ', continent: 'AFRICA' },
  { iso2Code: 'TH', translationKey: 'countries.TH', continent: 'ASIA' },
  { iso2Code: 'TL', translationKey: 'countries.TL', continent: 'ASIA' },
  { iso2Code: 'TG', translationKey: 'countries.TG', continent: 'AFRICA' },
  { iso2Code: 'TK', translationKey: 'countries.TK', continent: 'OCEANIA' },
  { iso2Code: 'TO', translationKey: 'countries.TO', continent: 'OCEANIA' },
  { iso2Code: 'TT', translationKey: 'countries.TT', continent: 'NORTH_AMERICA' },
  { iso2Code: 'TN', translationKey: 'countries.TN', continent: 'AFRICA' },
  { iso2Code: 'TR', translationKey: 'countries.TR', continent: 'ASIA' },
  { iso2Code: 'TM', translationKey: 'countries.TM', continent: 'ASIA' },
  { iso2Code: 'TC', translationKey: 'countries.TC', continent: 'NORTH_AMERICA' },
  { iso2Code: 'TV', translationKey: 'countries.TV', continent: 'OCEANIA' },
  { iso2Code: 'UG', translationKey: 'countries.UG', continent: 'AFRICA' },
  { iso2Code: 'UA', translationKey: 'countries.UA', continent: 'EUROPE' },
  { iso2Code: 'AE', translationKey: 'countries.AE', continent: 'ASIA' },
  { iso2Code: 'GB', translationKey: 'countries.GB', continent: 'EUROPE' },
  { iso2Code: 'US', translationKey: 'countries.US', continent: 'NORTH_AMERICA' },
  { iso2Code: 'UM', translationKey: 'countries.UM', continent: 'OCEANIA' },
  { iso2Code: 'UY', translationKey: 'countries.UY', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'UZ', translationKey: 'countries.UZ', continent: 'ASIA' },
  { iso2Code: 'VU', translationKey: 'countries.VU', continent: 'OCEANIA' },
  { iso2Code: 'VE', translationKey: 'countries.VE', continent: 'SOUTH_AMERICA' },
  { iso2Code: 'VN', translationKey: 'countries.VN', continent: 'ASIA' },
  { iso2Code: 'VG', translationKey: 'countries.VG', continent: 'NORTH_AMERICA' },
  { iso2Code: 'VI', translationKey: 'countries.VI', continent: 'NORTH_AMERICA' },
  { iso2Code: 'WF', translationKey: 'countries.WF', continent: 'OCEANIA' },
  { iso2Code: 'EH', translationKey: 'countries.EH', continent: 'AFRICA' },
  { iso2Code: 'YE', translationKey: 'countries.YE', continent: 'ASIA' },
  { iso2Code: 'ZM', translationKey: 'countries.ZM', continent: 'AFRICA' },
  { iso2Code: 'ZW', translationKey: 'countries.ZW', continent: 'AFRICA' },
];
